import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Loader2, ShieldAlert, ArrowLeft, BarChart3, Type, LayoutTemplate, ImageDown, SlidersHorizontal } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

// Management pages a granted team member (or admin) may reach WITHOUT the full admin dashboard.
const MANAGE_NAV = [
  { slug: "user-analytics", to: "/admin/user-analytics", label: "User Analytics", icon: BarChart3 },
  { slug: "font-management", to: "/admin/font-management", label: "Font Management", icon: Type },
  { slug: "template-management", to: "/admin/template-management", label: "Templates", icon: LayoutTemplate },
  { slug: "user-upload-review", to: "/admin/template-review", label: "Template Review", icon: ImageDown },
  { slug: "pages-access", to: "/admin/pages-access", label: "Pages & Access", icon: SlidersHorizontal },
];

// Generic guarded shell: verifies page access server-side and, for granted team
// members, exposes ONLY the management pages they can reach (never the full /admin).
export default function ManageShell({ slug, title, children }) {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [state, setState] = useState({ loading: true, allowed: false });
  const [navSlugs, setNavSlugs] = useState([]);

  useEffect(() => {
    if (loading) return;
    if (!user) { setState({ loading: false, allowed: false }); return; }
    let alive = true;
    (async () => {
      try {
        const acc = await api.pageAccess(slug);
        if (!alive) return;
        setState({ loading: false, allowed: !!acc.allowed });
        // build nav from the pages this user can view
        const checks = await Promise.all(MANAGE_NAV.map((n) =>
          api.pageAccess(n.slug).then((a) => (a.allowed ? n.slug : null)).catch(() => null)));
        if (alive) setNavSlugs(checks.filter(Boolean));
      } catch {
        if (alive) setState({ loading: false, allowed: false });
      }
    })();
    return () => { alive = false; };
  }, [slug, user, loading]);

  if (loading || state.loading) return <div className="flex min-h-screen items-center justify-center bg-[#0A0A0A]"><Loader2 className="animate-spin text-[#00F0FF]" size={26} /></div>;

  if (!state.allowed) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-[#0A0A0A] px-6 text-center" data-testid="manage-forbidden">
        <ShieldAlert size={40} className="text-[#00F0FF]" />
        <h1 className="font-display text-2xl font-black text-white">Access restricted</h1>
        <p className="max-w-md text-sm text-[#A1A1AA]">{user ? "You don't have permission to open this management page. Ask an admin to grant you access." : "Please sign in to continue."}</p>
        <button onClick={() => nav(user ? "/" : "/login")} className="mt-2 rounded-lg bg-[#00F0FF] px-4 py-2 text-sm font-bold text-black">{user ? "Go home" : "Sign in"}</button>
      </div>
    );
  }

  const visible = MANAGE_NAV.filter((n) => user?.role === "admin" || navSlugs.includes(n.slug));

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="manage-shell">
      <header className="sticky top-0 z-20 border-b border-white/10 bg-[#0A0A0A]/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-3 px-4 py-3 sm:px-6">
          <Link to="/" className="flex items-center gap-1.5 text-xs font-semibold text-[#A1A1AA] hover:text-white"><ArrowLeft size={14} /> Home</Link>
          <span className="hidden text-white/20 sm:inline">|</span>
          <span className="font-display text-sm font-black tracking-tight text-white">Team Management</span>
          <nav className="ml-auto flex flex-wrap gap-1.5" data-testid="manage-nav">
            {visible.map((n) => {
              const active = loc.pathname === n.to;
              return (
                <Link key={n.slug} to={n.to} data-testid={`manage-nav-${n.slug}`}
                  className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold ${active ? "bg-[#00F0FF] text-black" : "border border-white/10 text-[#A1A1AA] hover:text-white"}`}>
                  <n.icon size={13} /> {n.label}
                </Link>
              );
            })}
          </nav>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
        <h1 className="mb-5 font-display text-2xl font-black text-white" data-testid="manage-title">{title}</h1>
        {children}
      </main>
    </div>
  );
}
